import { useCallback, useMemo } from 'react';

import { useOrderGetAvailableStatuses, useOrderSaveStatus } from 'api/requests';
import { OrderStatusType } from 'api/types/entity/order/orderStatusType';
import { formatToISODate } from 'helpers';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';

import { ChangeStatusFormType } from '../forms/changeStatusForm';
import { OrderAvailableStatusesMap } from '../types';

interface Props {
  orderId: string;
  versionNumber?: number;
  onSuccessCallback: () => void;
}

export const useChangeStatus = ({
  onSuccessCallback,
  orderId,
  versionNumber,
}: Props) => {
  const notify = useNotify();
  const { t } = useTranslation();

  const { loading, availableStatuses } = useOrderGetAvailableStatuses(orderId);

  const { saveStatus, loading: submitLoading, error } = useOrderSaveStatus();

  const availableStatusesMap = useMemo(
    () =>
      availableStatuses.reduce((acc, status) => {
        acc[status.status] = {
          isRequiredReason: status.isRequiredReason,
          isRequiredFmNumber: status.isRequiredFmNumber,
          isRequiredClosedDate: status.isRequiredClosedDate,
        };
        return acc;
      }, {} as OrderAvailableStatusesMap),
    [availableStatuses]
  );

  const onSubmit: FormSubmit<ChangeStatusFormType> = useCallback(
    async (values) => {
      if (versionNumber) {
        const status = values.status.value;
        const chosenStatus = availableStatusesMap[status];

        await saveStatus(orderId, versionNumber, {
          value: status,
          closedDate: chosenStatus.isRequiredClosedDate
            ? formatToISODate(values.closedDate!)
            : undefined,
          fmNumber: chosenStatus.isRequiredFmNumber
            ? values.fmNumber!
            : undefined,
          reason: chosenStatus.isRequiredReason ? values.reason! : undefined,
        });

        if (status === OrderStatusType.InProgress) {
          notify.info(t('order.order.createBrightMessage'));
        }
        onSuccessCallback();
      }
    },
    [
      versionNumber,
      availableStatusesMap,
      saveStatus,
      orderId,
      onSuccessCallback,
      notify,
      t,
    ]
  );

  useServerErrorNotify(error);

  return {
    loading,
    submitLoading,
    availableStatusesMap,
    onSubmit,
  };
};
